import { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { artists, artistRewards, myArtistXP, allQuests } from "@/mocks/artistData";
import type { Reward } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";
import RewardDetailModal from "@/pages/rewards/components/RewardDetailModal";
import ArtistXPCard from "@/pages/rewards/artist/components/ArtistXPCard";
import ArtistRewardCard from "@/pages/rewards/artist/components/ArtistRewardCard";

type CategoryFilter = "all" | "offline" | "credit" | "digital" | "exclusive";

const categoryTabs: { key: CategoryFilter; label: string; icon: string }[] = [
  { key: "all", label: "전체", icon: "ri-apps-line" },
  { key: "offline", label: "오프라인 이벤트", icon: "ri-map-pin-line" },
  { key: "credit", label: "크레딧 등재", icon: "ri-film-line" },
  { key: "exclusive", label: "독점 콘텐츠", icon: "ri-lock-line" },
  { key: "digital", label: "디지털 굿즈", icon: "ri-star-line" },
];

export default function ArtistRewardsPage() {
  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>("all");
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [sortBy, setSortBy] = useState("xp-asc");

  const artist = artists.find((a) => a.id === artistId);
  const xpData = myArtistXP.find((x) => x.artistId === artistId);
  const artistQuestCount = allQuests.filter((q) => q.artistId === artistId).length;
  const myXP = xpData?.xp ?? 0;

  const filteredRewards = useMemo(() => {
    if (!artist) return [];
    let result = artistRewards.filter((r) => r.artistId === artistId);
    if (selectedCategory !== "all") result = result.filter((r) => r.category === selectedCategory);
    if (sortBy === "xp-asc") result.sort((a, b) => a.xpCost - b.xpCost);
    else if (sortBy === "xp-desc") result.sort((a, b) => b.xpCost - a.xpCost);
    else if (sortBy === "stock-asc") result.sort((a, b) => a.stock - b.stock);
    return result;
  }, [selectedCategory, sortBy, artistId, artist]);

  const redeemableCount = filteredRewards.filter((r) => r.xpCost <= myXP && r.stock > 0).length;
  const totalRewards = artistRewards.filter((r) => r.artistId === artistId).length;

  if (!artist) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-white/30 mb-4 block" />
          <p className="text-white/50">크리에이터를 찾을 수 없습니다.</p>
          <Link to="/artists" className="mt-4 inline-block text-sm text-white/40 hover:text-white underline cursor-pointer">
            크리에이터 목록으로
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden pt-20">
        <div className="absolute inset-0 h-[420px]">
          <img
            src={artist.coverImage}
            alt={artist.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/60 via-[#0a0a0f]/70 to-[#0a0a0f]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/80 via-transparent to-[#0a0a0f]/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-0">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-8">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/artists" className="hover:text-white/70 transition-colors cursor-pointer">크리에이터</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-white/70">{artist.name}</span>
            <i className="ri-arrow-right-s-line text-xs" />
            <span style={{ color: artist.accentColor }}>리워드</span>
          </div>

          {/* Artist Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6 pb-10">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 flex-shrink-0" style={{ borderColor: artist.accentColor }}>
              <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: `${artist.accentColor}20`, color: artist.accentColor, border: `1px solid ${artist.accentColor}40` }}
                >
                  {artist.categoryLabel}
                </span>
                {artist.verified && <i className="ri-verified-badge-fill" style={{ color: artist.accentColor }} />}
              </div>
              <h1 className="text-3xl md:text-4xl font-black text-white mb-1">{artist.name}</h1>
              <p className="text-white/50 text-sm">{artist.tagline}</p>
            </div>
            <div className="flex gap-3">
              <Link
                to={`/quests?artist=${artist.id}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-sword-line" />
                캠페인 참여
              </Link>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 text-white/40 hover:text-white/70 text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                뒤로
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-24">
        {/* XP Dashboard */}
        <div className="mb-10">
          <ArtistXPCard artist={artist} xpData={xpData} totalRewards={totalRewards} redeemableCount={redeemableCount} artistQuestCount={artistQuestCount} />
        </div>

        {/* Section Title */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-black text-white mb-1">
              {artist.name} 리워드
            </h2>
            <p className="text-white/40 text-sm">
              {artist.name} XP로만 교환 가능한 독점 혜택
            </p>
          </div>
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none pl-4 pr-8 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm focus:outline-none focus:border-white/30 cursor-pointer"
            >
              <option value="xp-asc">XP 낮은 순</option>
              <option value="xp-desc">XP 높은 순</option>
              <option value="stock-asc">잔여 수량 적은 순</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 flex-wrap mb-8">
          {categoryTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedCategory(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === tab.key
                  ? "font-bold text-[#0a0a0f]"
                  : "bg-white/5 text-white/50 border border-white/10 hover:text-white hover:border-white/20"
              }`}
              style={selectedCategory === tab.key ? { background: artist.accentColor } : {}}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Results Info */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-white/40 text-sm">
            <span className="text-white font-semibold">{filteredRewards.length}</span>개의 리워드
            {redeemableCount > 0 && (
              <span className="ml-2 text-[#39ff14] font-semibold">({redeemableCount}개 교환 가능)</span>
            )}
          </p>
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <i className="ri-information-line" />
            <span style={{ color: "#39ff14" }}>초록 테두리</span>
            <span>= 교환 가능</span>
          </div>
        </div>

        {/* Reward Grid */}
        {filteredRewards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRewards.map((reward) => (
              <ArtistRewardCard
                key={reward.id}
                reward={reward}
                artist={artist}
                myXP={myXP}
                onSelect={() => setSelectedReward(reward)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-white/30">
            <i className="ri-gift-line text-4xl mb-4 block" />
            <p>해당 카테고리의 리워드가 없습니다.</p>
          </div>
        )}

        {/* Quest CTA */}
        <div
          className="mt-16 rounded-2xl p-10 text-center relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${artist.accentColor}15, ${artist.secondaryColor}10)`, border: `1px solid ${artist.accentColor}30` }}
        >
          <div className="absolute inset-0 opacity-5">
            <img src={artist.coverImage} alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="relative z-10">
            <div
              className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-5"
              style={{ background: `${artist.accentColor}20`, border: `1px solid ${artist.accentColor}40` }}
            >
              <i className="ri-sword-line text-2xl" style={{ color: artist.accentColor }} />
            </div>
            <h3 className="text-white font-black text-xl mb-2">
              {artist.name} XP를 더 모아보세요
            </h3>
            <p className="text-white/40 text-sm mb-6 max-w-sm mx-auto">
              {artist.name}의 캠페인을 완료하면 {artist.name} XP가 적립됩니다.<br />
              XP를 모아 더 많은 리워드를 교환하세요.
            </p>
            <Link
              to={`/quests?artist=${artist.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap text-[#0a0a0f]"
              style={{ background: artist.accentColor }}
            >
              <i className="ri-sword-line" />
              {artist.name} 캠페인 참여하기
            </Link>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedReward && (
        <RewardDetailModal
          reward={selectedReward}
          artist={artist}
          myXP={myXP}
          onClose={() => setSelectedReward(null)}
        />
      )}
    </div>
  );
}
